import {
  Mesh,
  PlaneGeometry,
  MeshLambertMaterial,
  MeshPhongMaterial,
  Color,
  Scene,
  Group,
  BoxGeometry,
  CylinderGeometry,
  ConeGeometry,
  SphereGeometry,
  Float32BufferAttribute,
} from 'three';
import { TerrainType } from '../game/StageLoader';

const MAP_SIZE = 20000;

// ═══════════════════════════════════════════════════
// 共通マテリアル（使い回し）
// ═══════════════════════════════════════════════════
const matSand      = new MeshLambertMaterial({ color: 0xc4a265 });
const matSandDark  = new MeshLambertMaterial({ color: 0xaa8a50 });
const matSandLight = new MeshLambertMaterial({ color: 0xd4b87a });
const matRock      = new MeshLambertMaterial({ color: 0x8a6a4a });
const matRockDark  = new MeshLambertMaterial({ color: 0x6a4a2a });
const matRockGray  = new MeshLambertMaterial({ color: 0x7a7068 });
const matGrass     = new MeshLambertMaterial({ color: 0x6a8a4a });
const matAsphalt   = new MeshLambertMaterial({ color: 0x3a3a3a });
const matConcrete  = new MeshLambertMaterial({ color: 0x888882 });
const matMetal     = new MeshLambertMaterial({ color: 0x888880 });
const matMetalDark = new MeshLambertMaterial({ color: 0x555550 });
const matAdobe     = new MeshLambertMaterial({ color: 0xc49a6c });
const matRoof      = new MeshLambertMaterial({ color: 0xa07040 });
const matWater     = new MeshPhongMaterial({ color: 0x2266aa, shininess: 120, transparent: true, opacity: 0.75 });
const matTreeTrunk = new MeshLambertMaterial({ color: 0x5a4030 });
const matTreeLeaf  = new MeshLambertMaterial({ color: 0x3a7a30 });
const matRust      = new MeshLambertMaterial({ color: 0x8a5533 });
const matWhite     = new MeshPhongMaterial({ color: 0xffffff, emissive: 0x666666, emissiveIntensity: 0.3 });
const matRed       = new MeshLambertMaterial({ color: 0xcc2222 });

// ═══════════════════════════════════════════════════
// 地形ベース
// ═══════════════════════════════════════════════════
function buildGroundPlane(scene: Scene): Mesh {
  const geo = new PlaneGeometry(MAP_SIZE, MAP_SIZE);
  const mesh = new Mesh(geo, matSand);
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  scene.add(mesh);
  return mesh;
}

// 地面の色パッチ（砂の色ムラ）
function buildSandPatches(scene: Scene): void {
  const patches: [number, number, number, number, typeof matSandDark][] = [
    [-3000, -1500, 2800, 2000, matSandDark],
    [ 2000,  3000, 3500, 2500, matSandLight],
    [-5000,  4000, 2000, 1800, matSandDark],
    [ 4000, -3500, 2200, 1600, matSandLight],
    [-1000, -4000, 3000, 2000, matSandDark],
    [ 6000,  1000, 1500, 2500, matSandLight],
  ];
  for (const [x, z, w, d, mat] of patches) {
    const p = new Mesh(new PlaneGeometry(w, d), mat);
    p.rotation.x = -Math.PI / 2;
    p.position.set(x, 0.02, z);
    scene.add(p);
  }
}

// ═══════════════════════════════════════════════════
// 砂丘フィールド
// ═══════════════════════════════════════════════════
function buildSandDunes(scene: Scene): void {
  // 砂丘エリア1: 北東
  buildDuneField(scene, 3500, -2500, 8, 1.0);
  // 砂丘エリア2: 南西
  buildDuneField(scene, -4000, 3500, 6, 0.8);
  // 砂丘エリア3: 北西に小規模
  buildDuneField(scene, -6000, -4000, 4, 0.6);
}

function buildDuneField(scene: Scene, cx: number, cz: number, count: number, scale: number): void {
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + Math.sin(i * 3) * 0.5;
    const dist = 200 + i * 180 * scale;
    const x = cx + Math.cos(angle) * dist;
    const z = cz + Math.sin(angle) * dist;
    const w = (300 + i * 60) * scale;
    const h = (20 + i * 8) * scale;
    const d = (80 + i * 30) * scale;

    const dune = new Mesh(new SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), matSandLight);
    dune.scale.set(w, h, d);
    dune.position.set(x, 0, z);
    dune.rotation.y = angle + 0.3;
    scene.add(dune);
  }
}

// ═══════════════════════════════════════════════════
// 岩山群
// ═══════════════════════════════════════════════════
function buildRockyMountains(scene: Scene): void {
  const mountains: [number, number, number, number][] = [
    // 北西山脈
    [-3000, -2000, 400, 900],
    [-3400, -1600, 280, 650],
    [-2600, -2400, 200, 500],
    [-2900, -2800, 160, 380],
    // 東部山脈
    [ 4000,  1000, 500, 1100],
    [ 4500,   600, 300, 700],
    [ 3500,  1500, 220, 480],
    [ 4200,  1800, 180, 400],
    // 南部
    [-1500,  4000, 350, 800],
    [-1000,  4400, 240, 580],
    [-1800,  4600, 150, 350],
    // 北部
    [ 2000, -4500, 420, 950],
    [ 2400, -4800, 200, 420],
    [ 1500, -4200, 170, 380],
    // 南西
    [-4500,  3000, 380, 820],
    [-4800,  2600, 200, 450],
    // 中央南
    [    0, -5000, 460, 1000],
    [  400, -5300, 220, 550],
    // 遠方（境界付近）
    [ 7000, -2000, 350, 750],
    [-7000,  1000, 300, 600],
    [ 5000,  5000, 280, 620],
    [-5500, -5500, 320, 700],
  ];

  for (const [x, z, r, h] of mountains) {
    const g = new Group();
    const cone = new Mesh(new ConeGeometry(r, h, 7), matRock);
    cone.position.set(0, h / 2, 0);
    g.add(cone);
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const slab = new Mesh(new ConeGeometry(r * 0.35, h * 0.6, 5), matRockDark);
      slab.position.set(Math.cos(a) * r * 0.45, h * 0.3, Math.sin(a) * r * 0.45);
      slab.rotation.y = a;
      g.add(slab);
    }
    g.position.set(x, 0, z);
    scene.add(g);
  }
}

// 散在する岩（ボルダー）
function buildScatteredRocks(scene: Scene): void {
  const rocks: [number, number, number, number][] = [
    [500, 2500, 15, 8], [-800, 1200, 20, 12], [1800, -800, 18, 10],
    [-2000, 500, 25, 14], [3000, -1500, 12, 7], [-600, -3000, 22, 13],
    [4500, -800, 16, 9], [-3500, 1000, 20, 11], [1200, 3500, 14, 8],
    [6000, 3000, 18, 10], [-4000, -2000, 24, 15], [800, -6000, 20, 12],
    [-2500, -4500, 16, 9], [5500, -4000, 22, 13], [-6500, 2000, 18, 10],
    [3000, 6000, 14, 8], [-1500, 6500, 20, 11], [7000, 0, 15, 9],
  ];

  for (const [x, z, r, h] of rocks) {
    const g = new Group();
    const boulder = new Mesh(new SphereGeometry(r, 5, 4), matRockGray);
    boulder.scale.set(1, h / r, 1);
    boulder.position.set(0, h * 0.4, 0);
    g.add(boulder);
    // 小さい岩を周囲に
    for (let i = 0; i < 2; i++) {
      const a = i * Math.PI + Math.random();
      const sr = r * 0.4;
      const sm = new Mesh(new SphereGeometry(sr, 4, 3), matRockDark);
      sm.position.set(Math.cos(a) * r * 0.8, sr * 0.3, Math.sin(a) * r * 0.8);
      g.add(sm);
    }
    g.position.set(x, 0, z);
    scene.add(g);
  }
}

// ═══════════════════════════════════════════════════
// 軍事基地（滑走路 + 格納庫 + 管制塔 + 防壁）
// ═══════════════════════════════════════════════════
function buildMilitaryBase(scene: Scene): void {
  // 芝生
  const grass = new Mesh(new PlaneGeometry(1200, 2800), matGrass);
  grass.rotation.x = -Math.PI / 2;
  grass.position.set(0, 0.04, 600);
  scene.add(grass);

  // 滑走路
  const runway = new Mesh(new PlaneGeometry(65, 2200), matAsphalt);
  runway.rotation.x = -Math.PI / 2;
  runway.position.set(0, 0.08, 600);
  scene.add(runway);

  // センターライン
  for (let i = -8; i <= 8; i++) {
    const line = new Mesh(new PlaneGeometry(4, 70), matWhite);
    line.rotation.x = -Math.PI / 2;
    line.position.set(0, 0.12, 600 + i * 130);
    scene.add(line);
  }

  // 端マーカー
  for (const z of [-500, 1700]) {
    const marker = new Mesh(new PlaneGeometry(65, 14), matWhite);
    marker.rotation.x = -Math.PI / 2;
    marker.position.set(0, 0.12, z);
    scene.add(marker);
  }

  // 誘導路
  const taxiway = new Mesh(new PlaneGeometry(30, 400), matAsphalt);
  taxiway.rotation.x = -Math.PI / 2;
  taxiway.position.set(150, 0.07, 300);
  scene.add(taxiway);
  const taxiConnect = new Mesh(new PlaneGeometry(30, 160), matAsphalt);
  taxiConnect.rotation.x = -Math.PI / 2;
  taxiConnect.rotation.z = Math.PI / 2;
  taxiConnect.position.set(75, 0.07, 300);
  scene.add(taxiConnect);

  // エプロン（駐機場）
  const apron = new Mesh(new PlaneGeometry(300, 250), matConcrete);
  apron.rotation.x = -Math.PI / 2;
  apron.position.set(280, 0.06, 300);
  scene.add(apron);

  // 格納庫×3
  buildHangar(scene, -250, 150, 0.9);
  buildHangar(scene, 250, 150, 0.9);
  buildHangar(scene, 350, 500, 1.1);

  // 管制塔
  buildControlTower(scene, -350, 400);

  // 防壁（コの字型）
  for (let i = 0; i < 3; i++) {
    buildRevetment(scene, 180 + i * 100, 200);
  }

  // 燃料タンク
  for (let i = 0; i < 2; i++) {
    const tank = new Mesh(new CylinderGeometry(15, 15, 25, 12), matMetalDark);
    tank.position.set(-400 + i * 45, 12.5, 600);
    scene.add(tank);
  }

  // 風向きソック（ウインドソック用ポール）
  const pole = new Mesh(new CylinderGeometry(0.5, 0.5, 20, 6), matMetal);
  pole.position.set(60, 10, -300);
  scene.add(pole);
  const sock = new Mesh(new ConeGeometry(2, 8, 6), matRed);
  sock.position.set(60, 20, -304);
  sock.rotation.z = Math.PI / 2;
  scene.add(sock);
}

function buildHangar(scene: Scene, x: number, z: number, s: number): void {
  const g = new Group();
  const walls = new Mesh(new BoxGeometry(80 * s, 30 * s, 60 * s), matMetal);
  walls.position.set(0, 15 * s, 0);
  g.add(walls);
  const roof = new Mesh(new CylinderGeometry(42 * s, 42 * s, 62 * s, 8, 1, false, 0, Math.PI), matMetalDark);
  roof.rotation.z = Math.PI / 2;
  roof.rotation.y = Math.PI / 2;
  roof.position.set(0, 30 * s, 0);
  g.add(roof);
  const door = new Mesh(new BoxGeometry(40 * s, 24 * s, 2), new MeshLambertMaterial({ color: 0x444440 }));
  door.position.set(0, 12 * s, -31 * s);
  g.add(door);
  g.position.set(x, 0, z);
  scene.add(g);
}

function buildControlTower(scene: Scene, x: number, z: number): void {
  const g = new Group();
  // 塔脚
  const shaft = new Mesh(new BoxGeometry(15, 50, 15), matConcrete);
  shaft.position.set(0, 25, 0);
  g.add(shaft);
  // 管制室
  const cab = new Mesh(new BoxGeometry(25, 10, 25), matMetal);
  cab.position.set(0, 55, 0);
  g.add(cab);
  // 窓（ガラス帯）
  const glass = new Mesh(new BoxGeometry(26, 5, 26), new MeshPhongMaterial({
    color: 0x224466, emissive: 0x001122, shininess: 150, transparent: true, opacity: 0.6,
  }));
  glass.position.set(0, 57, 0);
  g.add(glass);
  // 屋根
  const roofTop = new Mesh(new BoxGeometry(28, 2, 28), matMetalDark);
  roofTop.position.set(0, 62, 0);
  g.add(roofTop);
  // アンテナ
  const ant = new Mesh(new CylinderGeometry(0.5, 0.5, 12, 6), matMetal);
  ant.position.set(0, 69, 0);
  g.add(ant);
  g.position.set(x, 0, z);
  scene.add(g);
}

function buildRevetment(scene: Scene, x: number, z: number): void {
  const g = new Group();
  const mat = matConcrete;
  // コの字防壁
  const w1 = new Mesh(new BoxGeometry(2, 6, 30), mat); w1.position.set(-12, 3, 0); g.add(w1);
  const w2 = new Mesh(new BoxGeometry(2, 6, 30), mat); w2.position.set( 12, 3, 0); g.add(w2);
  const w3 = new Mesh(new BoxGeometry(26, 6, 2), mat); w3.position.set(0, 3, 15); g.add(w3);
  g.position.set(x, 0, z);
  scene.add(g);
}

// ═══════════════════════════════════════════════════
// オアシス
// ═══════════════════════════════════════════════════
function buildOasis(scene: Scene, cx: number, cz: number): void {
  // 水面
  const water = new Mesh(new CylinderGeometry(120, 130, 1, 16), matWater);
  water.position.set(cx, 0.2, cz);
  scene.add(water);

  // 周囲の草地
  const grass = new Mesh(new CylinderGeometry(180, 200, 0.5, 12), matGrass);
  grass.position.set(cx, 0.05, cz);
  scene.add(grass);

  // ヤシの木
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const r = 100 + (i % 3) * 30;
    buildPalmTree(scene, cx + Math.cos(a) * r, cz + Math.sin(a) * r);
  }
}

function buildPalmTree(scene: Scene, x: number, z: number): void {
  const g = new Group();
  // 幹（少し傾斜）
  const trunk = new Mesh(new CylinderGeometry(1.5, 2.5, 30, 6), matTreeTrunk);
  trunk.position.set(0, 15, 0);
  trunk.rotation.z = (Math.random() - 0.5) * 0.15;
  g.add(trunk);
  // 葉（放射状の板）
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const leaf = new Mesh(new BoxGeometry(3, 0.3, 14), matTreeLeaf);
    leaf.position.set(Math.cos(a) * 4, 30, Math.sin(a) * 4);
    leaf.rotation.y = a;
    leaf.rotation.x = 0.6;
    g.add(leaf);
  }
  g.position.set(x, 0, z);
  scene.add(g);
}

// ═══════════════════════════════════════════════════
// 石油施設
// ═══════════════════════════════════════════════════
function buildOilFacility(scene: Scene, cx: number, cz: number): void {
  const g = new Group();

  // コンクリート敷地
  const pad = new Mesh(new PlaneGeometry(500, 400), matConcrete);
  pad.rotation.x = -Math.PI / 2;
  pad.position.set(0, 0.06, 0);
  g.add(pad);

  // タンク群
  for (let i = 0; i < 4; i++) {
    const tank = new Mesh(new CylinderGeometry(30, 30, 22, 14), matMetal);
    tank.position.set(-120 + i * 80, 11, -50);
    g.add(tank);
    const lid = new Mesh(new ConeGeometry(31, 5, 14), matMetalDark);
    lid.position.set(-120 + i * 80, 24, -50);
    g.add(lid);
  }

  // 精製塔
  for (let i = 0; i < 3; i++) {
    const tower = new Mesh(new CylinderGeometry(6, 8, 60 + i * 15, 8), matMetal);
    tower.position.set(-80 + i * 50, 30 + i * 7, 80);
    g.add(tower);
    // 配管
    const pipe = new Mesh(new CylinderGeometry(2, 2, 40, 6), matMetalDark);
    pipe.rotation.z = Math.PI / 2;
    pipe.position.set(-60 + i * 50, 20, 80);
    g.add(pipe);
  }

  // フレアスタック（煙突）
  const flare = new Mesh(new CylinderGeometry(3, 4, 80, 8), matRust);
  flare.position.set(150, 40, 30);
  g.add(flare);

  // 管理棟
  const office = new Mesh(new BoxGeometry(60, 12, 30), matConcrete);
  office.position.set(0, 6, 140);
  g.add(office);

  g.position.set(cx, 0, cz);
  scene.add(g);
}

// ═══════════════════════════════════════════════════
// 砂漠の集落
// ═══════════════════════════════════════════════════
function buildDesertVillage(scene: Scene, cx: number, cz: number, buildingCount: number): void {
  const layouts: [number, number, number, number, number][] = [
    [  0,   0,  30, 12, 28],
    [ 50,  20,  22, 10, 20],
    [-40,  10,  18, 15, 18],
    [ 25, -40,  26,  8, 24],
    [-20, -35,  20, 11, 20],
    [ 70, -20,  15,  9, 16],
    [-60, -10,  24, 13, 22],
    [ 40,  55,  16,  7, 14],
  ];

  for (let i = 0; i < Math.min(buildingCount, layouts.length); i++) {
    const [rx, rz, w, h, d] = layouts[i];
    const g = new Group();
    const walls = new Mesh(new BoxGeometry(w, h, d), matAdobe);
    walls.position.set(0, h / 2, 0);
    g.add(walls);
    const roof = new Mesh(new BoxGeometry(w + 2, 2, d + 2), matRoof);
    roof.position.set(0, h + 1, 0);
    g.add(roof);
    // ドア
    const door = new Mesh(new BoxGeometry(4, 6, 1), matRockDark);
    door.position.set(0, 3, -d / 2 - 0.1);
    g.add(door);
    g.position.set(cx + rx, 0, cz + rz);
    scene.add(g);
  }

  // 集落中央に井戸
  const well = new Mesh(new CylinderGeometry(4, 4, 3, 8), matRockGray);
  well.position.set(cx + 5, 1.5, cz - 10);
  scene.add(well);
}

// ═══════════════════════════════════════════════════
// 古代遺跡
// ═══════════════════════════════════════════════════
function buildRuins(scene: Scene, cx: number, cz: number): void {
  const matStone = new MeshLambertMaterial({ color: 0xb0a080 });
  const matStoneDark = new MeshLambertMaterial({ color: 0x908060 });

  const g = new Group();

  // 崩れた壁
  const walls: [number, number, number, number, number][] = [
    [0, 0, 60, 15, 3],
    [-30, 25, 3, 12, 50],
    [30, 25, 3, 8, 40],
    [0, 50, 50, 6, 3],
  ];
  for (const [rx, rz, w, h, d] of walls) {
    const wall = new Mesh(new BoxGeometry(w, h, d), matStone);
    wall.position.set(rx, h / 2, rz);
    g.add(wall);
  }

  // 柱（数本は倒壊）
  for (let i = 0; i < 6; i++) {
    const x = -20 + (i % 3) * 20;
    const z = 10 + Math.floor(i / 3) * 30;
    const pillar = new Mesh(new CylinderGeometry(2, 2.5, 18, 8), matStoneDark);

    if (i === 2 || i === 4) {
      // 倒壊した柱
      pillar.rotation.z = Math.PI / 2 + (i * 0.2);
      pillar.position.set(x, 2, z);
    } else {
      pillar.position.set(x, 9, z);
    }
    g.add(pillar);
  }

  // 台座
  const base = new Mesh(new BoxGeometry(70, 3, 60), matStoneDark);
  base.position.set(0, 1.5, 25);
  g.add(base);

  g.position.set(cx, 0, cz);
  scene.add(g);
}

// ═══════════════════════════════════════════════════
// 送電鉄塔
// ═══════════════════════════════════════════════════
function buildTowerLine(scene: Scene, positions: [number, number][]): void {
  for (const [x, z] of positions) {
    const g = new Group();
    const pole = new Mesh(new CylinderGeometry(1.5, 2.5, 60, 6), matMetal);
    pole.position.set(0, 30, 0);
    g.add(pole);
    const arm = new Mesh(new BoxGeometry(30, 2, 2), matMetal);
    arm.position.set(0, 60, 0);
    g.add(arm);
    const armLow = new Mesh(new BoxGeometry(20, 2, 2), matMetal);
    armLow.position.set(0, 50, 0);
    g.add(armLow);
    g.position.set(x, 0, z);
    scene.add(g);
  }
}

// ═══════════════════════════════════════════════════
// 干上がった川（ワジ = 涸れ川）
// ═══════════════════════════════════════════════════
function buildDriedRiver(scene: Scene): void {
  const matDry = new MeshLambertMaterial({ color: 0x9a8050 });
  // 蛇行する川を複数のセグメントで表現
  const points: [number, number, number][] = [
    [-5000, -7000, 120],
    [-4200, -5500, 140],
    [-3000, -4000, 130],
    [-2000, -2500, 150],
    [-1200, -1000, 140],
    [ -500,   500, 130],
    [  200,  2000, 120],
    [  800,  3500, 140],
    [ 1500,  5000, 130],
    [ 2200,  6500, 120],
  ];

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, z1, w1] = points[i];
    const [x2, z2, w2] = points[i + 1];
    const dx = x2 - x1, dz = z2 - z1;
    const len = Math.sqrt(dx * dx + dz * dz);
    const angle = Math.atan2(dx, dz);

    const seg = new Mesh(new PlaneGeometry((w1 + w2) / 2, len), matDry);
    seg.rotation.x = -Math.PI / 2;
    seg.rotation.z = -angle;
    seg.position.set((x1 + x2) / 2, 0.03, (z1 + z2) / 2);
    scene.add(seg);
  }
}

// ═══════════════════════════════════════════════════
// レーダーサイト
// ═══════════════════════════════════════════════════
function buildRadarSite(scene: Scene, x: number, z: number): void {
  const g = new Group();
  // レドーム
  const dome = new Mesh(new SphereGeometry(12, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), matWhite);
  dome.position.set(0, 8, 0);
  g.add(dome);
  // 台座
  const base = new Mesh(new CylinderGeometry(8, 10, 8, 8), matConcrete);
  base.position.set(0, 4, 0);
  g.add(base);
  // フェンス（簡易）
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const post = new Mesh(new CylinderGeometry(0.3, 0.3, 6, 4), matMetal);
    post.position.set(Math.cos(a) * 25, 3, Math.sin(a) * 25);
    g.add(post);
  }
  g.position.set(x, 0, z);
  scene.add(g);
}

// ═══════════════════════════════════════════════════
// Vector3 import for revetment
// ═══════════════════════════════════════════════════
import { Vector3 } from 'three';

// ═══════════════════════════════════════════════════
// メインクラス
// ═══════════════════════════════════════════════════
// ═══════════════════════════════════════════════════
// 海上マテリアル
// ═══════════════════════════════════════════════════
const matOcean     = new MeshPhongMaterial({ color: 0x1a5588, shininess: 100, transparent: true, opacity: 0.85 });
const matShallow   = new MeshPhongMaterial({ color: 0x3a88bb, shininess: 80, transparent: true, opacity: 0.75 });
const matIslandRock = new MeshLambertMaterial({ color: 0x6a6a5a });
const matIslandGrass = new MeshLambertMaterial({ color: 0x4a8a3a });
const matIslandSand = new MeshLambertMaterial({ color: 0xccaa66 });
const matPlatformMetal = new MeshLambertMaterial({ color: 0x666660 });

// ═══════════════════════════════════════════════════
// 海上地形
// ═══════════════════════════════════════════════════
function buildOceanPlane(scene: Scene): Mesh {
  const geo = new PlaneGeometry(MAP_SIZE, MAP_SIZE);
  const mesh = new Mesh(geo, matOcean);
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  scene.add(mesh);
  return mesh;
}

function buildIsland(scene: Scene, cx: number, cz: number, scale: number): void {
  const g = new Group();
  // 砂浜ベース
  const beach = new Mesh(new CylinderGeometry(80 * scale, 100 * scale, 4, 12), matIslandSand);
  beach.position.set(0, 1, 0);
  g.add(beach);
  // 岩山
  const rock = new Mesh(new ConeGeometry(50 * scale, 80 * scale, 8), matIslandRock);
  rock.position.set(0, 42 * scale, 0);
  g.add(rock);
  // 緑
  const green = new Mesh(new SphereGeometry(35 * scale, 8, 6), matIslandGrass);
  green.position.set(10 * scale, 30 * scale, 5 * scale);
  green.scale.set(1, 0.6, 1);
  g.add(green);
  // 浅瀬リング
  const shallow = new Mesh(new CylinderGeometry(120 * scale, 140 * scale, 1, 16), matShallow);
  shallow.position.set(0, -0.5, 0);
  g.add(shallow);
  g.position.set(cx, 0, cz);
  scene.add(g);
}

function buildOilPlatform(scene: Scene, cx: number, cz: number): void {
  const g = new Group();
  // 支柱×4
  for (const [dx, dz] of [[-15, -15], [15, -15], [-15, 15], [15, 15]]) {
    const leg = new Mesh(new CylinderGeometry(1.5, 1.5, 40, 6), matMetalDark);
    leg.position.set(dx, 20, dz);
    g.add(leg);
  }
  // デッキ
  const deck = new Mesh(new BoxGeometry(40, 2, 40), matPlatformMetal);
  deck.position.set(0, 40, 0);
  g.add(deck);
  // 上部構造
  const tower = new Mesh(new BoxGeometry(8, 20, 8), matMetal);
  tower.position.set(-10, 51, -10);
  g.add(tower);
  const crane = new Mesh(new BoxGeometry(2, 30, 2), matMetalDark);
  crane.position.set(10, 55, 5);
  g.add(crane);
  // ヘリパッド
  const pad = new Mesh(new CylinderGeometry(8, 8, 0.5, 16), matAsphalt);
  pad.position.set(5, 41.3, 8);
  g.add(pad);
  g.position.set(cx, 0, cz);
  scene.add(g);
}

function buildBuoys(scene: Scene): void {
  const positions: [number, number][] = [
    [800, 1200], [-1200, 800], [2500, -600], [-600, -2000],
    [3500, 2000], [-3000, 3000], [500, -4000], [-4500, -1000],
  ];
  for (const [x, z] of positions) {
    const buoy = new Mesh(new SphereGeometry(3, 6, 4), matRed);
    buoy.position.set(x, 2, z);
    scene.add(buoy);
    const pole = new Mesh(new CylinderGeometry(0.3, 0.3, 6, 4), matWhite);
    pole.position.set(x, 5, z);
    scene.add(pole);
  }
}

function buildOceanTerrain(scene: Scene): void {
  // 島×5
  buildIsland(scene, 3000, -2000, 1.2);
  buildIsland(scene, -4000, 1500, 1.0);
  buildIsland(scene, -1000, -4500, 0.8);
  buildIsland(scene, 5000, 3000, 1.5);
  buildIsland(scene, -6000, -3000, 0.7);
  // 洋上プラットフォーム×3
  buildOilPlatform(scene, 2000, 1500);
  buildOilPlatform(scene, -3000, -2000);
  buildOilPlatform(scene, -500, 5000);
  // ブイ
  buildBuoys(scene);
}

// ═══════════════════════════════════════════════════
// 山岳地形用 Perlin ノイズ（簡易実装）
// ═══════════════════════════════════════════════════
function fade(t: number): number { return t * t * t * (t * (t * 6 - 15) + 10); }
function lerp2(a: number, b: number, t: number): number { return a + t * (b - a); }

const PERM: number[] = [];
{
  const p = Array.from({ length: 256 }, (_, i) => i);
  // deterministic shuffle (seed = 42)
  let seed = 42;
  for (let i = 255; i > 0; i--) {
    seed = (seed * 16807 + 0) % 2147483647;
    const j = seed % (i + 1);
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 512; i++) PERM.push(p[i & 255]);
}

function grad(hash: number, x: number, z: number): number {
  const h = hash & 3;
  const u = h < 2 ? x : z;
  const v = h < 2 ? z : x;
  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}

function perlin2D(x: number, z: number): number {
  const xi = Math.floor(x) & 255;
  const zi = Math.floor(z) & 255;
  const xf = x - Math.floor(x);
  const zf = z - Math.floor(z);
  const u = fade(xf);
  const v = fade(zf);
  const aa = PERM[PERM[xi] + zi];
  const ab = PERM[PERM[xi] + zi + 1];
  const ba = PERM[PERM[xi + 1] + zi];
  const bb = PERM[PERM[xi + 1] + zi + 1];
  return lerp2(
    lerp2(grad(aa, xf, zf), grad(ba, xf - 1, zf), u),
    lerp2(grad(ab, xf, zf - 1), grad(bb, xf - 1, zf - 1), u),
    v
  );
}

/** フラクタルノイズ（オクターブ重ね合わせ） */
function fbm(x: number, z: number, octaves: number, lacunarity: number, gain: number): number {
  let val = 0, amp = 1, freq = 1, maxAmp = 0;
  for (let i = 0; i < octaves; i++) {
    val += perlin2D(x * freq, z * freq) * amp;
    maxAmp += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return val / maxAmp;
}

const MOUNTAIN_SCALE = 0.0004;  // ノイズのスケール
const MOUNTAIN_HEIGHT = 600;    // 最大高さ

function mountainHeight(x: number, z: number): number {
  const n = fbm(x * MOUNTAIN_SCALE, z * MOUNTAIN_SCALE, 5, 2.0, 0.5);
  // -1〜1 を 0〜1 にマッピングして高度に変換
  const h = Math.max(0, (n + 0.3) * 0.7) * MOUNTAIN_HEIGHT;
  // マップ境界付近はフェードアウト
  const bx = 1 - Math.pow(Math.max(0, Math.abs(x) / 9000 - 0.2), 2);
  const bz = 1 - Math.pow(Math.max(0, Math.abs(z) / 9000 - 0.2), 2);
  return h * Math.max(0, bx) * Math.max(0, bz);
}

// ═══════════════════════════════════════════════════
// 山岳地形ビルダー
// ═══════════════════════════════════════════════════
const matMountainGrass = new MeshLambertMaterial({ color: 0x4a6a3a });
const matMountainRock  = new MeshLambertMaterial({ color: 0x6a6a5a });
const matSnow          = new MeshLambertMaterial({ color: 0xdde8ee });
const matForest        = new MeshLambertMaterial({ color: 0x2a5a22 });

function buildMountainPlane(scene: Scene): Mesh {
  const segments = 128;
  const geo = new PlaneGeometry(MAP_SIZE, MAP_SIZE, segments, segments);
  geo.rotateX(-Math.PI / 2);

  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    pos.setY(i, mountainHeight(x, z));
  }
  geo.computeVertexNormals();

  // 高さに応じて頂点カラーを設定
  const colors = new Float32Array(pos.count * 3);
  const grassC = new Color(0x4a6a3a);
  const rockC  = new Color(0x6a6a5a);
  const snowC  = new Color(0xdde8ee);
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    let c: Color;
    if (y > 400) c = snowC;
    else if (y > 200) c = rockC;
    else c = grassC;
    colors[i * 3]     = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new Float32BufferAttribute(colors, 3));

  const mat = new MeshLambertMaterial({ vertexColors: true });
  const mesh = new Mesh(geo, mat);
  mesh.receiveShadow = true;
  scene.add(mesh);
  return mesh;
}

function buildMountainObjects(scene: Scene): void {
  // 森林クラスタ（低地に木々）
  const treePosns: [number, number][] = [
    [-2000, 1000], [3000, -2000], [-4000, -3000], [1000, 4000],
    [5000, 1000], [-1000, -5000], [-6000, 2000], [4000, -5000],
  ];
  for (const [tx, tz] of treePosns) {
    const h = mountainHeight(tx, tz);
    if (h > 250) continue; // 高地には木を置かない
    const cluster = new Group();
    for (let i = 0; i < 12; i++) {
      const ox = (Math.random() - 0.5) * 400;
      const oz = (Math.random() - 0.5) * 400;
      const ch = mountainHeight(tx + ox, tz + oz);
      const tree = new Group();
      const trunk = new Mesh(new CylinderGeometry(3, 4, 30, 5), matTreeTrunk);
      trunk.position.y = 15;
      tree.add(trunk);
      const crown = new Mesh(new ConeGeometry(15, 35, 6), matForest);
      crown.position.y = 40;
      tree.add(crown);
      tree.position.set(ox, ch, oz);
      tree.scale.setScalar(0.7 + Math.random() * 0.6);
      cluster.add(tree);
    }
    cluster.position.set(tx, 0, tz);
    scene.add(cluster);
  }

  // 敵基地施設（渓谷の平地に配置）
  const basePosns: [number, number][] = [
    [0, 0], [-3000, 3000], [4000, 2000], [-2000, -4000],
  ];
  for (const [bx, bz] of basePosns) {
    const bh = mountainHeight(bx, bz);
    // コンクリートパッド
    const pad = new Mesh(new BoxGeometry(200, 2, 200), matConcrete);
    pad.position.set(bx, bh + 1, bz);
    scene.add(pad);
    // 建物
    const bld = new Mesh(new BoxGeometry(40, 20, 60), matMetal);
    bld.position.set(bx + 40, bh + 10, bz);
    scene.add(bld);
    const bld2 = new Mesh(new BoxGeometry(30, 15, 40), matMetalDark);
    bld2.position.set(bx - 50, bh + 7.5, bz + 30);
    scene.add(bld2);
  }
}

export class Ground {
  readonly mesh: Mesh;
  private terrainType: TerrainType;

  constructor(scene: Scene, terrain: TerrainType = 'desert') {
    this.terrainType = terrain;

    if (terrain === 'ocean') {
      this.mesh = buildOceanPlane(scene);
      buildOceanTerrain(scene);
    } else if (terrain === 'mountain') {
      this.mesh = buildMountainPlane(scene);
      buildMountainObjects(scene);
    } else {
      this.mesh = buildGroundPlane(scene);
      buildSandPatches(scene);
      buildSandDunes(scene);
      buildRockyMountains(scene);
      buildScatteredRocks(scene);
      buildMilitaryBase(scene);
      buildOasis(scene, -2500, 800);
      buildOasis(scene, 5500, -2500);
      buildOilFacility(scene, 6000, 4000);
      buildDesertVillage(scene, -1500, 1500, 6);
      buildDesertVillage(scene,  2500, -1000, 5);
      buildDesertVillage(scene, -4500, -800, 4);
      buildDesertVillage(scene,  1000, 5500, 3);
      buildRuins(scene, -3000, 2500);
      buildRuins(scene,  5000, -5000);
      buildTowerLine(scene, [
        [1000, -1000], [1400, -600], [1800, -200], [2200, 200], [2600, 600],
      ]);
      buildTowerLine(scene, [
        [-3000, -500], [-3500, 0], [-4000, 500], [-4500, 1000],
      ]);
      buildDriedRiver(scene);
      buildRadarSite(scene, -6000, -1500);
      buildRadarSite(scene,  3000,  3500);
    }
  }

  getHeightAt(x: number, z: number): number {
    if (this.terrainType === 'mountain') {
      return mountainHeight(x, z);
    }
    return 0;
  }
}
